import React, { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import {
  ShieldCheck,
  AlertCircle,
  Clock,
  History,
  Calendar,
  FileText,
  Gavel,
  ChevronDown,
  Activity,
  UserCheck
} from "lucide-react";
import { makeRequestAuth } from "../../../_config/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";

/* ------------------ CONSTANTS ------------------ */

const EMPTY_FORM = {
  committedCrime: false,
  arrestedForOffence: false,
  chargedForOffence: false,
  convictedForOffence: false,
  criminalDetails: {
    committedCrime: { date: "", reason: "", result: "" },
    arrestedForOffence: { date: "", reason: "", result: "" },
    chargedForOffence: { date: "", reason: "", result: "" },
    convictedForOffence: { date: "", reason: "", result: "" }
  }
};

const STATUS_CONFIG = {
  pending: { bg: "bg-amber-50 text-amber-700 border-amber-200", icon: <Clock size={14} />, label: "Pending Verification" },
  approve: { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: <ShieldCheck size={14} />, label: "Security Cleared" },
  reject: { bg: "bg-rose-50 text-rose-700 border-rose-200", icon: <AlertCircle size={14} />, label: "Action Required" }
};

const CriminalitySecurityForm = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCriminalitySecurity = async () => {
      setLoading(true);
      try {
        const res = await makeRequestAuth("get", "/user/getCriminalitySecurity");
        if (res?.data) {
          const apiData = res.data;
          const mappedDetails = { ...EMPTY_FORM.criminalDetails };

          if (Array.isArray(apiData.criminalDetails)) {
            apiData.criminalDetails.forEach((item) => {
              if (mappedDetails[item.type]) {
                mappedDetails[item.type] = {
                  date: item.date ? new Date(item.date).toISOString().split('T')[0] : "",
                  reason: item.reason || "",
                  result: item.result || "",
                };
              }
            });
          }

          setForm({
            committedCrime: apiData.committedCrime || false,
            arrestedForOffence: apiData.arrestedForOffence || false,
            chargedForOffence: apiData.chargedForOffence || false,
            convictedForOffence: apiData.convictedForOffence || false,
            criminalDetails: mappedDetails
          });
          setPreview(apiData);
        }
      } catch (err) { console.log("Fetch Error"); }
      finally { setLoading(false); }
    };
    fetchCriminalitySecurity();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleDetailChange = (type, field, value) => {
    setForm((prev) => ({
      ...prev,
      criminalDetails: { ...prev.criminalDetails, [type]: { ...prev.criminalDetails[type], [field]: value } }
    }));
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   setLoading(true);
  //   try {
  //     const detailsArray = Object.keys(form.criminalDetails)
  //       .filter(type => form[type])
  //       .map(type => ({ type, ...form.criminalDetails[type] }));

  //     const payload = { ...form, criminalDetails: detailsArray };
  //     const res = await makeRequestAuth("post", "/user/createOrUpdateCriminalitySecurity", payload);
  //     setPreview(res?.data);
  //     showSuccessToast("Security declaration saved");
  //   } catch (err) {
  //     showErrorToast(err?.response?.data?.message || "Submission failed");
  //   } finally { setLoading(false); }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const detailsArray = [];

    for (const type of Object.keys(form.criminalDetails)) {
      if (form[type]) {
        const detail = form.criminalDetails[type];

        if (!detail.date) {
          showErrorToast("Please select date for " + type);
          return;
        }

        if (!detail.reason.trim()) {
          showErrorToast("Please enter reason for " + type);
          return;
        }

        if (!detail.result.trim()) {
          showErrorToast("Please enter result for " + type);
          return;
        }

        detailsArray.push({
          type,
          date: detail.date,
          reason: detail.reason,
          result: detail.result
        });
      }
    }

    setLoading(true);

    try {
      const payload = {
        committedCrime: form.committedCrime,
        arrestedForOffence: form.arrestedForOffence,
        chargedForOffence: form.chargedForOffence,
        convictedForOffence: form.convictedForOffence,
        criminalDetails: detailsArray
      };

      const res = await makeRequestAuth(
        "post",
        "/user/createOrUpdateCriminalitySecurity",
        payload
      );

      setPreview(res?.data);
      showSuccessToast("Security declaration saved");

    } catch (err) {
      const errorMessage = err?.response?.data?.message;

      console.log('errorMessage', errorMessage);

      if (typeof errorMessage === "object" && errorMessage !== null) {
        Object.values(errorMessage).forEach((msg) => {
          showErrorToast(msg);
        });
      } else {
        showErrorToast(errorMessage || "Submission failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-slate-50 py-6 md:py-12 px-3 sm:px-6">
      <ToastContainer position="top-right" />

      <div className="max-w-4xl mx-auto">
        {/* HEADER */}
        <div className="bg-[#132b45] rounded-t-[1.5rem] md:rounded-t-[2.5rem] p-6 md:p-10 text-white shadow-2xl relative">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 relative z-10">
            <div className="flex flex-col sm:flex-row items-center gap-5 text-center sm:text-left">
              <div className="bg-white/10 p-4 rounded-2xl backdrop-blur-xl border border-white/20">
                <ShieldCheck size={36} className="text-blue-300" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">Security Declaration</h1>
                <p className="text-xs text-blue-100/50 uppercase tracking-[0.2em] font-bold mt-1">Background & Legal Verification</p>
              </div>
            </div>
            {preview?.status && (
              <div className={`px-5 py-2.5 rounded-xl border text-[11px] font-black uppercase tracking-widest flex items-center gap-2 shadow-sm ${STATUS_CONFIG[preview.status]?.bg}`}>
                {STATUS_CONFIG[preview.status]?.icon} {STATUS_CONFIG[preview.status]?.label}
              </div>
            )}
          </div>
        </div>

        {/* CONTENT */}
        <div className="bg-white rounded-b-[1.5rem] md:rounded-b-[2.5rem] border-x border-b border-slate-200 p-5 md:p-12 shadow-xl">
          {preview?.status ? (
            /* --- PREVIEW MODE --- */
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <PreviewCard label="Committed Crime" value={preview.committedCrime} data={preview.criminalDetails?.find(d => d.type === "committedCrime")} />
                <PreviewCard label="Arrest Record" value={preview.arrestedForOffence} data={preview.criminalDetails?.find(d => d.type === "arrestedForOffence")} />
                <PreviewCard label="Legal Charges" value={preview.chargedForOffence} data={preview.criminalDetails?.find(d => d.type === "chargedForOffence")} />
                <PreviewCard label="Prior Convictions" value={preview.convictedForOffence} data={preview.criminalDetails?.find(d => d.type === "convictedForOffence")} />
              </div>

              {(preview.status === "reject") && (
                <div className="mt-10 pt-8 border-t border-slate-100 flex justify-center">
                  <button onClick={() => setPreview(null)} className="group flex items-center gap-3 px-8 py-4 bg-[#132b45] text-white rounded-2xl hover:bg-slate-800 transition-all font-bold shadow-lg">
                    <History size={20} className="group-hover:rotate-[-45deg] transition-transform" />
                    Update Records
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* --- FORM MODE --- */
            <form onSubmit={handleSubmit} className="space-y-10">
              <div className="bg-blue-50/50 p-4 rounded-2xl border border-blue-100 flex gap-3 items-start mb-8">
                <UserCheck size={20} className="text-blue-500 shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700 leading-relaxed font-medium">
                  Please declare any legal history accurately. Failure to disclose information may result in automatic rejection of your application.
                </p>
              </div>

              <div className="space-y-10">
                <QuestionBlock label="Have you ever committed a criminal offence?" field="committedCrime" value={form.committedCrime} details={form.criminalDetails.committedCrime} onChange={handleChange} onDetailChange={handleDetailChange} />
                <QuestionBlock label="Have you ever been arrested for an offence?" field="arrestedForOffence" value={form.arrestedForOffence} details={form.criminalDetails.arrestedForOffence} onChange={handleChange} onDetailChange={handleDetailChange} />
                <QuestionBlock label="Have you ever been charged with an offence?" field="chargedForOffence" value={form.chargedForOffence} details={form.criminalDetails.chargedForOffence} onChange={handleChange} onDetailChange={handleDetailChange} />
                <QuestionBlock label="Have you ever been convicted of an offence?" field="convictedForOffence" value={form.convictedForOffence} details={form.criminalDetails.convictedForOffence} onChange={handleChange} onDetailChange={handleDetailChange} />
              </div>

              <button type="submit" className="w-full bg-[#132b45] text-white py-5 rounded-2xl font-black text-lg shadow-xl hover:bg-slate-800 transition-all active:scale-[0.98] flex items-center justify-center gap-3 mt-12">
                <ShieldCheck size={24} /> Submit Security Declaration
              </button>
            </form>
          )}
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center z-[100]">
          <div className="bg-white p-8 rounded-3xl shadow-2xl flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-slate-100 border-t-blue-600 rounded-full animate-spin mb-4"></div>
            <p className="text-slate-800 font-bold">Securing Records...</p>
          </div>
        </div>
      )}
    </div>
  );
};

/* ------------------ REUSABLE FORM BLOCKS ------------------ */

const QuestionBlock = ({ label, field, value, details, onChange, onDetailChange }) => (
  <div className="group animate-in fade-in slide-in-from-bottom-2 duration-500">
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5 px-1">
      <label className="text-base md:text-lg font-bold text-slate-800 max-w-xl">{label}</label>
      <div className="flex bg-slate-100 p-1 rounded-xl w-fit">
        <button type="button" onClick={() => onChange(field, false)} className={`px-6 py-2 rounded-lg text-sm font-black transition-all ${!value ? "bg-white text-slate-800 shadow-sm" : "text-slate-400"}`}>NO</button>
        <button type="button" onClick={() => onChange(field, true)} className={`px-6 py-2 rounded-lg text-sm font-black transition-all ${value ? "bg-rose-500 text-white shadow-md" : "text-slate-400"}`}>YES</button>
      </div>
    </div>

    {value && (
      <div className="p-5 md:p-8 bg-slate-50 border-2 border-dashed border-slate-200 rounded-[2rem] space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-4">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Event Date</label>
            <input type="date" value={details.date} onChange={(e) => onDetailChange(field, "date", e.target.value)} className="w-full border-2 border-white rounded-xl px-4 py-3.5 outline-none focus:ring-4 focus:ring-blue-100 bg-white font-bold text-slate-700 shadow-sm transition-all" />
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Detailed Reason</label>
              <textarea placeholder="Describe the incident..." rows="3" value={details.reason} onChange={(e) => onDetailChange(field, "reason", e.target.value)} className="w-full border-2 border-white rounded-xl px-4 py-3 outline-none focus:ring-4 focus:ring-blue-100 bg-white resize-none font-medium shadow-sm" />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Legal Outcome</label>
              <textarea placeholder="Final result/status..." rows="3" value={details.result} onChange={(e) => onDetailChange(field, "result", e.target.value)} className="w-full border-2 border-white rounded-xl px-4 py-3 outline-none focus:ring-4 focus:ring-blue-100 bg-white resize-none font-medium shadow-sm" />
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
);

/* ------------------ REUSABLE PREVIEW CARD ------------------ */

const PreviewCard = ({ label, value, data }) => {
  return (
    <div className={`relative flex flex-col rounded-[1.5rem] md:rounded-[2rem] border-2 transition-all p-6 ${value ? "bg-slate-50/50 border-slate-100" : "bg-rose-50/20 border-rose-100 shadow-sm"}`}>
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] mb-1">{label.replace(/([A-Z])/g, ' $1')}</p>
          <p className={`text-xl font-black leading-tight ${value ? 'text-emerald-600' : 'text-rose-600'}`}>{value ? "Record Declared" : "No Records"}</p>
        </div>
        <div className={`p-3 rounded-2xl shrink-0 ${value ? 'bg-emerald-500 text-white shadow-sm' : 'bg-rose-500 text-white'}`}>
          {value ? <ShieldCheck size={22} /> : <AlertCircle size={22} />}
        </div>
      </div>

      {value && data && (
        <div className="mt-4 pt-5 border-t border-green-100/50 space-y-4">
          <div className="flex items-center gap-2 bg-white/80 w-fit px-3 py-1.5 rounded-lg border border-green-100/50">
            <Calendar size={14} className="text-green-500" />
            <span className="text-xs font-bold text-slate-700">
              {data.date ? new Date(data.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <ScrollableInfo icon={<FileText size={12} />} label="Case Details" text={data.reason} />
            <ScrollableInfo icon={<Gavel size={12} />} label="Legal Status" text={data.result} />
          </div>
        </div>
      )}

    </div>
  );
};

const ScrollableInfo = ({ icon, label, text }) => (
  <div className="space-y-1.5">
    <p className="text-[10px] font-black text-green-400 uppercase flex items-center gap-1.5 px-1 tracking-wider">
      {icon} {label}
    </p>
    <div className="max-h-[80px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-green-200 scrollbar-track-transparent">
      <p className="text-xs text-slate-600 leading-relaxed break-words whitespace-pre-wrap">
        {text || "N/A"}
      </p>
    </div>
  </div>
);

export default CriminalitySecurityForm;